import {
  MAX_PIN_PBKDF2_ITERATIONS,
  PIN_HASH_BYTES,
  PIN_PBKDF2_ITERATIONS,
  PIN_SALT_BYTES,
  base64ToBytes,
} from "./pinCrypto";
import type { ChildControls, ParentPinRecord, ParentSettingsV1 } from "./parentTypes";

export class ParentSettingsValidationError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "ParentSettingsValidationError";
  }
}

function fail(message: string): never {
  throw new ParentSettingsValidationError(message);
}

function strictRecord(
  value: unknown,
  keys: readonly string[],
  label: string,
): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail(`${label} is invalid.`);
  const record = value as Record<string, unknown>;
  const actualKeys = Object.keys(record);
  if (
    actualKeys.length !== keys.length ||
    actualKeys.some((key) => !keys.includes(key)) ||
    keys.some((key) => !Object.hasOwn(record, key))
  ) {
    fail(`${label} contains unexpected or missing fields.`);
  }
  return record;
}

function boolean(value: unknown, label: string): boolean {
  if (typeof value !== "boolean") fail(`${label} must be true or false.`);
  return value;
}

export function validateChildControls(value: unknown): ChildControls {
  const controls = strictRecord(
    value,
    [
      "allowNewDrawings",
      "allowNewComics",
      "allowActivities",
      "allowProjectDeletion",
      "allowBackupImport",
      "allowBackupExport",
      "studioToolLevel",
      "allowDestructiveEditing",
    ],
    "Child controls",
  );
  const result: ChildControls = {
    allowNewDrawings: boolean(controls.allowNewDrawings, "Allow new drawings"),
    allowNewComics: boolean(controls.allowNewComics, "Allow new comics"),
    allowActivities: boolean(controls.allowActivities, "Allow activities"),
    allowProjectDeletion: boolean(controls.allowProjectDeletion, "Allow project deletion"),
    allowBackupImport: boolean(controls.allowBackupImport, "Allow backup import"),
    allowBackupExport: boolean(controls.allowBackupExport, "Allow backup export"),
    studioToolLevel:
      controls.studioToolLevel === "basic" ||
      controls.studioToolLevel === "creative" ||
      controls.studioToolLevel === "full"
        ? controls.studioToolLevel
        : fail("Studio tool level is invalid."),
    allowDestructiveEditing: boolean(controls.allowDestructiveEditing, "Allow destructive editing"),
  };
  if (!result.allowNewDrawings && !result.allowNewComics && !result.allowActivities) {
    fail("At least one creation path must remain available.");
  }
  return result;
}

export function validateParentPinRecord(value: unknown): ParentPinRecord {
  const pin = strictRecord(value, ["saltBase64", "hashBase64", "iterations"], "PIN settings");
  if (typeof pin.saltBase64 !== "string" || typeof pin.hashBase64 !== "string") {
    fail("PIN encoding is invalid.");
  }
  let salt: Uint8Array;
  let hash: Uint8Array;
  try {
    salt = base64ToBytes(pin.saltBase64, PIN_SALT_BYTES);
    hash = base64ToBytes(pin.hashBase64, PIN_HASH_BYTES);
  } catch {
    return fail("PIN encoding is invalid.");
  }
  if (salt.byteLength !== PIN_SALT_BYTES) fail("PIN salt length is invalid.");
  if (hash.byteLength !== PIN_HASH_BYTES) fail("PIN hash length is invalid.");
  if (
    !Number.isInteger(pin.iterations) ||
    (pin.iterations as number) < PIN_PBKDF2_ITERATIONS ||
    (pin.iterations as number) > MAX_PIN_PBKDF2_ITERATIONS
  ) {
    fail("PIN iteration count is invalid.");
  }
  return {
    saltBase64: pin.saltBase64,
    hashBase64: pin.hashBase64,
    iterations: pin.iterations as number,
  };
}

export function validateParentSettings(value: unknown): ParentSettingsV1 {
  const settings = strictRecord(value, ["version", "pin", "controls"], "Parent settings");
  if (settings.version !== 1) fail("Parent settings version is unsupported.");
  return {
    version: 1,
    pin: validateParentPinRecord(settings.pin),
    controls: validateChildControls(settings.controls),
  };
}

export function parseParentSettingsJson(json: string): ParentSettingsV1 {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json) as unknown;
  } catch {
    return fail("Parent settings JSON is corrupted.");
  }
  return validateParentSettings(parsed);
}

export function serializeParentSettings(settings: ParentSettingsV1): string {
  const validated = validateParentSettings(settings);
  return JSON.stringify({
    version: validated.version,
    pin: {
      saltBase64: validated.pin.saltBase64,
      hashBase64: validated.pin.hashBase64,
      iterations: validated.pin.iterations,
    },
    controls: {
      allowNewDrawings: validated.controls.allowNewDrawings,
      allowNewComics: validated.controls.allowNewComics,
      allowActivities: validated.controls.allowActivities,
      allowProjectDeletion: validated.controls.allowProjectDeletion,
      allowBackupImport: validated.controls.allowBackupImport,
      allowBackupExport: validated.controls.allowBackupExport,
      studioToolLevel: validated.controls.studioToolLevel,
      allowDestructiveEditing: validated.controls.allowDestructiveEditing,
    },
  });
}
